/**
 * Sidebar drawer for variant option "Learn more" content (Condition, Battery, SIM).
 * Opens from right; shows dropdown for option type and value, then image + long description.
 */
class VariantOptionSidebar extends HTMLElement {
  #data = null;
  #abort = null;
  #typeSelect = null;
  #valueSelect = null;
  #valuesGroup = null;
  #contentImage = null;
  #contentImg = null;
  #contentLong = null;
  #productOverride = null;

  open(optionType) {
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('variant-option-sidebar--open');
    document.body.classList.add('variant-option-sidebar-open');
    if (!this.#data) return;
    if (this.#typeSelect) this.#typeSelect.value = optionType || (this.#typeSelect.options[0]?.value ?? '');
    this.#populateValues(optionType);
    const currentVal = this.#currentValueForType(optionType);
    if (this.#valueSelect) {
      const firstOpt = this.#valueSelect.options[0]?.value;
      this.#valueSelect.value = (currentVal && Array.from(this.#valueSelect.options).some((o) => o.value === currentVal)) ? currentVal : firstOpt || '';
    }
    this.#showContent(optionType, this.#valueSelect?.value);
    if (this.#valuesGroup) this.#valuesGroup.hidden = !optionType;
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('variant-option-sidebar--open');
    document.body.classList.remove('variant-option-sidebar-open');
  }

  #populateValues(optionType) {
    if (!this.#valueSelect || !this.#data?.options) return;
    const list = this.#data.options[optionType];
    this.#valueSelect.innerHTML = '';
    if (list) {
      list.forEach((item) => {
        const opt = document.createElement('option');
        opt.value = item.label;
        opt.textContent = item.label;
        opt.dataset.key = item.key;
        this.#valueSelect.appendChild(opt);
      });
    }
  }

  #currentValueForType(optionType) {
    if (optionType === 'SIM' && this.#data?.currentSim) return this.#data.currentSim;
    if (optionType === 'Battery' && this.#data?.currentBattery) return this.#data.currentBattery;
    if (!this.#data?.optionNames || !this.#data?.currentVariant) return null;
    const idx = this.#data.optionNames.indexOf(optionType);
    if (idx === -1) return null;
    const key = 'option' + (idx + 1);
    return this.#data.currentVariant[key] || null;
  }

  #showContent(optionType, valueLabel) {
    const list = this.#data?.options?.[optionType];
    if (!list) return;
    const item = list.find((i) => i.label === valueLabel);
    if (this.#contentImage) this.#contentImage.hidden = !item?.image;
    if (this.#contentImg && item?.image) {
      this.#contentImg.src = item.image;
      this.#contentImg.alt = valueLabel || '';
    }
    if (this.#contentLong) {
      this.#contentLong.innerHTML = item?.long || '';
    }
  }

  connectedCallback() {
    const script = this.querySelector('script[data-sidebar-data]') || document.querySelector('script[data-sidebar-data]');
    if (!script) return;
    try {
      this.#data = JSON.parse(script.textContent);
    } catch (_) {
      return;
    }
    const overrideScript = this.querySelector('script[data-sidebar-product-override]') || document.querySelector('script[data-sidebar-product-override]');
    if (overrideScript) {
      try {
        const override = JSON.parse(overrideScript.textContent);
        if (override && typeof override === 'object') {
          this.#productOverride = override;
          for (const type of ['Condition', 'Battery', 'SIM']) {
            const list = this.#data.options?.[type];
            if (!list) continue;
            list.forEach((item) => {
              const o = override[item.key];
              if (o && typeof o === 'object') {
                if (o.long != null) item.long = o.long;
                if (o.image != null) item.image = o.image;
              }
            });
          }
        }
      } catch (_) {}
    }
    this.#typeSelect = this.querySelector('[data-option-type]');
    this.#valueSelect = this.querySelector('[data-option-value]');
    this.#valuesGroup = this.querySelector('[data-values-group]');
    this.#contentImage = this.querySelector('[data-content-image]');
    this.#contentImg = this.querySelector('[data-content-img]');
    this.#contentLong = this.querySelector('[data-content-long]');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(
      'click',
      (e) => {
        const learnMore = e.target.closest('[data-learn-more]');
        if (learnMore) {
          e.preventDefault();
          e.stopPropagation();
          const sidebar = document.getElementById('variant-option-sidebar');
          if (sidebar && typeof sidebar.open === 'function') {
            sidebar.open(learnMore.getAttribute('data-option-type'));
          }
        }
      },
      { signal, capture: true }
    );

    this.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('[data-close]')) this.close();
      },
      { signal }
    );

    if (this.#typeSelect) {
      this.#typeSelect.addEventListener(
        'change',
        () => {
          const type = this.#typeSelect.value;
          this.#valuesGroup.hidden = !type;
          this.#populateValues(type);
          const currentVal = this.#currentValueForType(type);
          if (this.#valueSelect) {
            const firstOpt = this.#valueSelect.options[0]?.value;
            this.#valueSelect.value = (currentVal && Array.from(this.#valueSelect.options).some((o) => o.value === currentVal)) ? currentVal : firstOpt || '';
            this.#showContent(type, this.#valueSelect.value);
          }
        },
        { signal }
      );
    }

    if (this.#valueSelect) {
      this.#valueSelect.addEventListener(
        'change',
        () => this.#showContent(this.#typeSelect?.value, this.#valueSelect.value),
        { signal }
      );
    }

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.classList.contains('variant-option-sidebar--open')) this.close();
      },
      { signal }
    );

    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    const updateShortDescriptions = (variant) => {
      if (!this.#productOverride || !variant || !this.#data?.optionNames) return;
      this.#data.optionNames.forEach((optionName, i) => {
        const val = variant['option' + (i + 1)];
        if (val == null) return;
        const key = (optionName + '_' + val).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
        const o = this.#productOverride[key];
        if (!o?.short) return;
        const descEl = section?.querySelector(
          `.variant-option-description[data-option-name="${optionName}"] .variant-option-description__content`
        );
        if (descEl) descEl.innerHTML = o.short;
      });
    };

    section?.addEventListener(
      'variant:update',
      (e) => {
        const data = e.detail?.data;
        if (data?.productId !== productId) return;
        const variant = e.detail?.resource;
        if (variant && this.#data?.currentVariant) {
          this.#data.currentVariant.option1 = variant.option1;
          this.#data.currentVariant.option2 = variant.option2;
          this.#data.currentVariant.option3 = variant.option3;
        }
        updateShortDescriptions(variant);
      },
      { signal }
    );

    if (this.#productOverride && this.#data?.currentVariant) {
      updateShortDescriptions(this.#data.currentVariant);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('variant-option-sidebar')) {
  customElements.define('variant-option-sidebar', VariantOptionSidebar);
}
